<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import { UiMessage, UiMessageType } from '@/types/chat';
import ChatMessageUser from '@/V2/chat/components/messages/ChatMessageUser.vue';
import ChatMessageAssistant from '@/V2/chat/components/messages/ChatMessageAssistant.vue';
import ChatMessageAssistantFooter from '@/V2/chat/components/messages/ChatMessageAssistantFooter.vue';
import ChatMessageTool from '@/V2/chat/components/messages/ChatMessageTool.vue';
import ChatPanelTip from '@/V2/chat/components/ChatPanelTip.vue';

interface Props {
  conversationUuid: string;
}

const props = defineProps<Props>();
const store = useStore();

const isGenerating = computed<boolean>(() =>
  store.getters.getIsGeneratingForConversationUuid(props.conversationUuid),
);
const messages = computed<UiMessage[]>(() =>
  store.getters.getMessagesForConversationUuid(props.conversationUuid),
);
const error = computed<string | null>(() =>
  store.getters.getErrorForConversationUuid(props.conversationUuid),
);
const currentChatTip = computed(() => store.getters.currentChatTip);
const tipShouldAppear = computed(() => store.getters.tipShouldAppear);
const shouldShowTip = computed(
  () => tipShouldAppear.value && currentChatTip.value && !isGenerating.value,
);

function shouldShowMessageFooter(message: UiMessage) {
  // Show the footer for any finished Assistant Message that is either
  // the latest Assistant Message in the conversation, or a previous
  // Assistant Message directly followed by a User Message.
  if (message.type !== UiMessageType.AssistantMessage) {
    return false;
  }
  const messageIndex = messages.value.findIndex(
    (msg) => msg.type === message.type && msg.content === message.content,
  );
  if (messageIndex === -1) {
    return false;
  }
  const nextMessage = messages.value[messageIndex + 1];
  if (nextMessage && nextMessage.type === UiMessageType.UserMessage) {
    return true;
  }
  const isLatestMessage = messageIndex === messages.value.length - 1;
  if (isLatestMessage && !isGenerating.value) {
    return true;
  }
  return false;
}
</script>

<template>
  <div v-if="messages.length > 0" class="chat-panel-messages i-mb-4 i-mx-9">
    <!-- messages -->
    <div class="message-area">
      <!-- Message list -->
      <div
        v-for="(message, msgIndex) in messages"
        :key="msgIndex"
        class="message-group i-mb-3 d-flex flex-column i-gap-2 i-p-2"
      >
        <chat-message-user
          v-if="message.type === UiMessageType.UserMessage"
          :message="message"
        />

        <chat-message-assistant
          v-else-if="message.type === UiMessageType.AssistantMessage"
          :message="message"
        />
        <chat-message-assistant-footer
          v-if="
            message.type === UiMessageType.AssistantMessage &&
            shouldShowMessageFooter(message)
          "
          :conversation-uuid="conversationUuid"
          :message-index="msgIndex"
          class="i-mt-3"
        />

        <chat-message-tool
          v-else-if="message.type === UiMessageType.ToolMessage"
          :message="message"
        />
      </div>

      <div v-if="error" class="error-message text-center i-text-body-2">
        {{ error }}
      </div>

      <div
        v-if="isGenerating"
        class="loading-indicator text-left i-text-body-2 i-p-2"
      >
        Thinking<span class="ellipsis" aria-hidden="true"></span>
      </div>

      <chat-panel-tip v-if="shouldShowTip" :tip="currentChatTip" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chat-panel-messages {
  position: relative;

  .message-area {
    .error-message {
      color: var(--text-error);
    }
  }

  .loading-indicator {
    color: var(--text-neutral-subtle);

    .ellipsis::after {
      content: '';
      display: inline-block;
      // set reserved width to zero so layout doesn't shift as dots animate
      width: 1ch;
      text-align: left;
      animation: ellipsisAnimation 1.6s steps(4, end) infinite;
    }
  }
}

@keyframes ellipsisAnimation {
  0% {
    content: '';
  }
  25% {
    content: '.';
  }
  50% {
    content: '..';
  }
  75%,
  100% {
    content: '...';
  }
}
</style>
